import {doingLogout} from '@/stores/auth-store';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {ConfirmInfo, validateConfirmInfo} from "@/hooks/use-confirm-on-click.ts";

const state = {
  loader: false,
  pageClass: '',
  showConfirm: null as null | string,
  confirmInfo: null as null | ConfirmInfo,
  pageTitle: '',
  redirect: null as null | string,
  appVersion: ''
};

type State = typeof state;

export const useUiStore = create<State>()(
  immer(
    devtools(
      () => state,
      {enabled: true, name: 'uiStore'}
    )
  )
);

export function setAppVersion(version: string) {
  return useUiStore.setState(state => {
    state.appVersion = version;
  });
}

export function showLoader(show = false, source = '') {
  return useUiStore.setState(state => {
    state.loader = show;
    if (show) {
      document.body.classList.add('loading');
    } else {
      document.body.classList.remove('loading');
    }
  }, false, {type: 'uiStore/showLoader', show, source});
}

export function setRedirect(redirect: string | null) {
  return useUiStore.setState(state => {
    state.redirect = redirect;
  }, false, {type: 'uiStore/setRedirect', redirect});
}

export function setLogoutRedirect(setFrom = true, source = '') {
  const params = new URLSearchParams();
  if (setFrom) {
    let from = window.location.pathname;
    if (window.location.search) {
      from += '?' + window.location.search;
    }
    params.set('from', from);
  }
  params.set('logout', '1');
  const url = '/login?' + params.toString();
  doingLogout(true, source);
  showLoader(false);
  setRedirect(url);
}

export function setThemeLight() {
  document.getElementsByTagName('html')[0].classList.remove('dark');
  document.getElementsByTagName('html')[0].classList.add('light');
}

export function setThemeDark() {
  document.getElementsByTagName('html')[0].classList.add('dark');
  document.getElementsByTagName('html')[0].classList.remove('light');
}

export function setPageClass(className: string) {
  return useUiStore.setState(state => {
    if (state.pageClass) {
      document.body.classList.remove(state.pageClass);
    }
    state.pageClass = className;
    document.body.classList.add(state.pageClass);
  }, false, {type: 'uiStore/setPageClass', className});
}

export function setPageTitle(pageTitle: string) {
  return useUiStore.setState(state => {
    state.pageTitle = pageTitle;
    document.title = pageTitle + ' - CGX';
  }, false, {type: 'uiStore/setPageTitle', heading: pageTitle});
}

export function setConfirmInfo(info: ConfirmInfo | null) {
  return useUiStore.setState(state => {
    state.confirmInfo = validateConfirmInfo(info);
  }, false, {type: 'uiStore/setConfirmInfo', info});
}